import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";
import { BooleanInput } from "@angular/cdk/coercion";

import {combineLatest, EMPTY, Subject} from "rxjs";
import {takeUntil, tap, catchError} from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";

import { AccountService } from "app/core/services/account/account.service";
import { MatDialog } from "@angular/material/dialog";
import { CreateAccountModalComponent } from "app/modules/pages/primary-account/create-account-modal/create-account-modal.component";
import { UserProfileService } from "app/core/services/user-profile/user-profile.service";
import { DEFAULT_AFTER_LOGIN } from "app/core/config/app.config";
import { RosterService } from "../../../core/services/roster/roster.service";
import { HeaderButtonService } from "../../../core/services/header-with-button/header-with-button.service";

@Component({
  selector: "primary-account-menu",
  templateUrl: "./primary-account-menu.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: "primaryAccountMenu",
})
export class PrimaryAccountMenuComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar: boolean = true;

  currentAccount: any = {};
  menuOpened = false;
  accountList: any = [];

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private userProfileService: UserProfileService,
    private dialog: MatDialog,
    private rosterService: RosterService,
    private _changeDetectorRef: ChangeDetectorRef,
    private headerButtonService: HeaderButtonService,
    private router: Router,
    private accountService: AccountService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    combineLatest([
      this.accountService.currentAccount,
      this.accountService.listAccounts,
    ])
      .pipe(
        tap((res: any) => {
          console.log(res);
          if (res[0]?.uuid) {
            this.currentAccount = { ...res[0] };
          }
          if (this.currentAccount?.uuid && res[1]) {
            this.accountList = res[1];
          }
          this._changeDetectorRef.markForCheck();
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe();
  }

  handleNewPrimaryAccount(): void {
    // this.accountService.getAlertForCreateAccount();
    const dialogRef = this.dialog.open(CreateAccountModalComponent, {
      data: {
        profile: this.userProfileService.currentUser.getValue()
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.accountService.getAllAccounts()
        .pipe(
          takeUntil(this._unsubscribeAll),
          catchError((error) => {
            console.error('Error refreshing account list:', error);
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  handleSwitchAccount(account): void {
    // TEZY2-4394 — accounts the user has been deactivated from are not selectable.
    if (this.isAccountDeactivated(account)) {
      return;
    }
    this.goToCurrentAccount(account);
  }

  /**
   * TEZY2-4394 — Is the logged-in user's membership of this account deactivated?
   * Deactivated accounts are shown greyed out and cannot be switched to.
   */
  isAccountDeactivated(account: any): boolean {
    const userId = this.userProfileService.currentUser.getValue()?.id;
    return this.accountService.isMemberDeactivated(account, userId);
  }

  /**
   * Tooltip explaining why an account row is disabled (deactivated membership
   * takes precedence over awaiting-verification); empty string means no tooltip.
   */
  accountTooltip(account: any): string {
    if (this.isAccountDeactivated(account)) {
      return "shell.accountMenu.deactivatedTooltip";
    }
    if (account?.verification?.status === "pending") {
      return "shell.accountMenu.pendingTooltip";
    }
    return "";
  }

  isOpened(e) {
    this.menuOpened = true;
  }

  goToCurrentAccount(account) {
    const needsAuthorityVerification =
      account?.verification?.status === '' || account?.verification?.status === 'declined';

    if (needsAuthorityVerification) {
      const dialogRef = this.dialog.open(CreateAccountModalComponent, {
        data: {
          profile: this.userProfileService.currentUser.getValue(),
          account,
          openAuthorityVerification: true,
        },
      });

      dialogRef.afterClosed().subscribe(() => {
        this.accountService
          .getAllAccounts()
          .pipe(
            takeUntil(this._unsubscribeAll),
            catchError((error) => {
              console.error('Error refreshing account list:', error);
              return EMPTY;
            })
          )
          .subscribe();
      });
      return;
    }

    this.accountService
      .setCurrentAccount(account.uuid)
      .pipe(
        catchError((err) => {
          if (err.status === 401) {
            this.snackBar.open(
              this.translate.instant("shell.accountMenu.noLongerAdmin"),
              "X",
              {
                duration: 2000,
                verticalPosition: "top",
                horizontalPosition: "center",
              }
            );
          }
          return EMPTY;
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.router.navigate([DEFAULT_AFTER_LOGIN]);
      });
  }

  statusIcon(account: any): string {
    if (account?.verification?.status === '' || account?.verification?.status === 'not-submitted') {
      return 'mat_outline:error_outline';
    } else if (account?.verification?.status === 'pending') {
      return 'mat_outline:pending';
    } else if (account?.verification?.status === 'declined') {
      return 'mat_outline:remove_circle_outline';
    } else if (account?.verification?.status === 'accepted' && account?.subscription && account?.subscription !== 0) {
      return 'mat_outline:verified';
    }
  }

  isClosed(e) {
    this.menuOpened = false;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }
}
